import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";
import {
  type CSSProperties,
  type FC,
  type ReactNode,
  type RefCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Cell,
  Collection,
  Column,
  type Key,
  Link,
  type LinkProps,
  Row,
  type SortDescriptor,
  Table,
  TableBody,
  TableHeader,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { Size } from "../helpers/helpers.js";
import { useInfiniteScroll } from "../helpers/useInfiniteScroll.js";
import { useLinkRender } from "../Link/routerLink.js";
import { nestedControlsStyle } from "../nestedControls.js";
import { TableSkeletonContent } from "../Skeletons/skeletons.js";
import { EntityTableEmptyState } from "./entityTableEmptyState.js";

/** Styles for a table with comfortable spacing and low information density, used for projects, teammates, etc. */
export const entityTable = tv({
  slots: {
    table: "isolate w-full text-sm",
    header: "sticky top-0 z-20",
    // The divider is an inset shadow on each cell rather than a border on the
    // header: in a collapsed-border table the border is painted by the table
    // and stays put while the header is sticky.
    column:
      "truncate bg-gray-50 py-3 text-left text-xs font-semibold text-foreground shadow-[inset_0_-1px_0_var(--color-border-light)] data-[allows-sorting]:cursor-pointer data-[allows-sorting]:hover:text-muted-foreground",
    body: "divide-y divide-gray-200",
    row: "group/row data-[react-aria-pressable]:cursor-pointer",
    cell: "bg-white group-data-[react-aria-pressable]/row:group-hover/row:bg-blue-50",
    empty: "bg-white",
  },
  variants: {
    // When rows act as links (via `EntityTableRowLink` in a cell) the row needs
    // to be the positioned ancestor so the link's `::before` overlay can expand
    // to cover it. We also mirror the hover/cursor affordance that pressable
    // rows get, since these rows aren't `react-aria` pressable.
    hasRowLinks: {
      true: {
        row: nestedControlsStyle.container(),
        cell: "group-[:hover]/row:bg-blue-50",
      },
    },
    size: {
      [Size.MEDIUM]: {
        column: "px-3 sm:px-6",
        cell: "px-3 py-2 sm:px-6 sm:py-4",
      },
      [Size.SMALL]: {
        column: "px-3 sm:px-4",
        cell: "px-3 py-2 sm:px-4",
      },
    },
    isFullBleed: {
      false: {
        table: "rounded-md shadow-sm ring-1 ring-black/5",
        column: "first:rounded-tl-md last:rounded-tr-md",
        empty: "rounded-b-md",
      },
      true: {},
    },
    isLastRow: {
      true: {},
    },
    isPinnedToLeft: {
      true: {
        column: "sticky z-30",
        cell: "sticky z-10",
      },
    },
    isLastPinnedToLeft: {
      true: {
        // Stops 1px short of the bottom so the fade doesn't cover the next
        // cell's divider.
        column:
          "overflow-visible after:pointer-events-none after:absolute after:top-0 after:-right-6 after:bottom-px after:w-6 after:bg-inherit after:[mask-image:linear-gradient(to_right,black,transparent)] after:content-['']",
        cell: "after:pointer-events-none after:absolute after:inset-y-0 after:-right-4 after:w-4 after:bg-inherit after:[mask-image:linear-gradient(to_right,black,transparent)] after:content-['']",
      },
    },
  },
  compoundVariants: [
    {
      isFullBleed: false,
      isLastRow: true,
      class: {
        cell: "first:rounded-bl-md last:rounded-br-md",
      },
    },
  ],
  defaultVariants: {
    size: Size.MEDIUM,
    isFullBleed: false,
    hasRowLinks: false,
  },
});

export interface EntityTableProps<T extends Entity> {
  /** Accessibility label for the table. */
  "aria-label": string;
  /** Should be stable to avoid frequent re-rendering of the table. */
  columns: readonly EntityTableColumnDefinition<T>[];
  entities: readonly T[] | undefined;
  /** Optional hint to help trigger pagination after more entities were loaded but all are hidden from view. */
  unfilteredEntityCount?: number;
  /** Whether entities are loading. */
  isLoading?: boolean;
  /** Whether an error occurred while loading entities. */
  isError?: boolean;
  /** A callback to load more entities, if more are available. */
  onLoadMore?: () => void;
  /** Message to show when there are no entities (and we're not loading or encountered an error). */
  emptyMessage: ReactNode;
  /**
   * Called when the user activates a row (click, Enter, etc.).
   *
   * Prefer `hasRowLinks` + `EntityTableRowLink` when activating a row should
   * navigate somewhere, so users can cmd/ctrl/right/middle-click the row.
   */
  onEntityAction?: (entity: T) => void;
  /**
   * Set this when rows navigate somewhere via an `EntityTableRowLink` rendered
   * in one of the columns. It gives the row the positioning required for the
   * link's overlay to cover the whole row, plus the hover/cursor affordance.
   */
  hasRowLinks?: boolean;
  /** Size variant for the table, affecting spacing and information density. */
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  /**
   * Optional per-row emphasis: returns extra classes merged onto every cell in
   * the row (cells carry the row's background, so this is where a row shade must
   * land). Use for subtle row-level distinction, e.g. shading a secondary row.
   */
  getRowClassName?: (entity: T) => string | undefined;
  /** Whether the table will be rendered full bleed and therefore should not have rounded corners. */
  isFullBleed?: boolean;
  /** Current sort descriptor. When provided alongside `onSortChange`, columns with `allowsSorting` show sort affordance. */
  sortDescriptor?: SortDescriptor;
  /** Called when the user clicks a sortable column header. */
  onSortChange?: (descriptor: SortDescriptor) => void;
}

interface Entity {
  id: string;
}

export interface EntityTableColumnDefinition<T extends Entity> {
  id: string;
  /** `className` for the column header cell. */
  className?: string;
  /** Content for the column header cell. */
  header: ReactNode;
  /** Whether this column acts as a header for the row. */
  isRowHeader?: boolean;
  /** Render text value for the cell. Used for search and type-ahead. */
  getTextValue?: (entity: T) => string;
  /** Whether this column header is clickable to change the sort order. */
  allowsSorting?: boolean;
  /**
   * Pins this column to the left edge while the table scrolls horizontally.
   * Pinned columns must form a contiguous prefix of the column list.
   */
  pinnedToLeft?: boolean;
  /** Render content for the column data cell.  */
  renderCell: (entity: T) => ReactNode;
}

const NO_ENTITIES = Object.freeze([]);

type TableElement = HTMLDivElement | HTMLTableElement;
type ColumnElement = HTMLDivElement | HTMLTableCellElement;

function pinnedColumnOffsetProperty(
  index: number,
): `--entity-table-pinned-left-${number}` {
  return `--entity-table-pinned-left-${index}`;
}

function usePinnedColumnLayout<T extends Entity>(
  columns: readonly EntityTableColumnDefinition<T>[],
) {
  const tableRef = useRef<TableElement>(null);
  const [pinnedHeaderElements, setPinnedHeaderElements] = useState<
    ReadonlyMap<string, ColumnElement>
  >(() => new Map());

  const pinnedColumns = useMemo(
    () => columns.filter((column) => column.pinnedToLeft),
    [columns],
  );
  // The final pinned column's width does not contribute to another column's
  // offset. A table with one pinned column therefore needs no measurement or
  // observer at all.
  const offsetColumns = useMemo(
    () => pinnedColumns.slice(0, -1),
    [pinnedColumns],
  );
  const pinnedColumnStyles = useMemo(() => {
    const styles = new Map<string, Pick<CSSProperties, "left">>();
    for (const [index, column] of pinnedColumns.entries()) {
      styles.set(column.id, {
        left: `var(${pinnedColumnOffsetProperty(index)}, 0px)`,
      });
    }
    return styles;
  }, [pinnedColumns]);

  const pinnedHeaderRefs = useMemo(
    () =>
      new Map<string, RefCallback<ColumnElement>>(
        offsetColumns.map((column) => [
          column.id,
          (element) => {
            setPinnedHeaderElements((currentElements) => {
              const nextElements = new Map(currentElements);
              if (element) {
                nextElements.set(column.id, element);
              } else {
                nextElements.delete(column.id);
              }
              return nextElements;
            });
          },
        ]),
      ),
    [offsetColumns],
  );

  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) {
      return;
    }

    const updateOffsets = () => {
      // ResizeObserver runs after layout. Read every width before writing any
      // styles so this cannot create layout thrash inside the callback.
      const widths = offsetColumns.map(
        (column) =>
          pinnedHeaderElements.get(column.id)?.getBoundingClientRect().width ??
          0,
      );
      let offset = 0;
      for (const [index, column] of pinnedColumns.entries()) {
        const property = pinnedColumnOffsetProperty(index);
        const value = `${offset}px`;
        if (table.style.getPropertyValue(property) !== value) {
          table.style.setProperty(property, value);
        }
        offset += widths[index] ?? 0;
      }
    };

    const clearOffsets = () => {
      for (const index of pinnedColumns.keys()) {
        table.style.removeProperty(pinnedColumnOffsetProperty(index));
      }
    };

    updateOffsets();

    const window = table.ownerDocument.defaultView;
    if (!window || offsetColumns.length === 0) {
      return clearOffsets;
    }

    const observer = new window.ResizeObserver(updateOffsets);

    for (const column of offsetColumns) {
      const element = pinnedHeaderElements.get(column.id);
      if (element) {
        observer.observe(element, { box: "border-box" });
      }
    }

    return () => {
      observer.disconnect();
      clearOffsets();
    };
  }, [offsetColumns, pinnedColumns, pinnedHeaderElements]);

  return {
    lastPinnedColumnId: pinnedColumns.at(-1)?.id,
    pinnedColumnStyles,
    pinnedHeaderRefs,
    tableRef,
  };
}

/**
 * Simple all-batteries-included table that can be used for projects, teammates, etc.
 *
 * Rows can either trigger an action via `onEntityAction`, or navigate somewhere
 * by setting `hasRowLinks` and rendering an {@link EntityTableRowLink} in one of
 * the columns (typically the row header). The latter is preferable for
 * navigation because the row becomes a real link, so users can
 * cmd/ctrl/right/middle-click it to open it in a new tab.
 *
 * If you need something even slightly more complicated, don't hesitate to fork this component and reuse its parts.
 */
export const EntityTable = <T extends Entity>(
  props: EntityTableProps<T>,
): ReactNode => {
  const { canLoadMore, loadMoreSentinelRef } = useInfiniteScroll(props);

  const { entities = NO_ENTITIES, onEntityAction } = props;
  const styles = entityTable({
    size: props.size,
    isFullBleed: props.isFullBleed,
    hasRowLinks: props.hasRowLinks,
  });
  const { lastPinnedColumnId, pinnedColumnStyles, pinnedHeaderRefs, tableRef } =
    usePinnedColumnLayout(props.columns);

  const onRowAction = onEntityAction
    ? (key: Key) => {
        const entity = entities.find((e) => e.id === key);
        if (entity) {
          onEntityAction(entity);
        }
      }
    : undefined;

  return (
    <Table
      ref={tableRef}
      aria-label={props["aria-label"]}
      className={styles.table()}
      // Lets a scrolling frame skip its own left edge fade: the last pinned
      // column already draws one.
      data-pinned-columns={lastPinnedColumnId !== undefined || undefined}
      onRowAction={onRowAction}
      sortDescriptor={props.sortDescriptor}
      onSortChange={props.onSortChange}
    >
      <TableHeader columns={props.columns} className={styles.header()}>
        {(column) => (
          <Column
            ref={pinnedHeaderRefs.get(column.id)}
            id={column.id}
            isRowHeader={column.isRowHeader}
            allowsSorting={column.allowsSorting}
            style={pinnedColumnStyles.get(column.id)}
            className={styles.column({
              isPinnedToLeft: column.pinnedToLeft,
              isLastPinnedToLeft: column.id === lastPinnedColumnId,
              className: column.className,
            })}
          >
            {({ allowsSorting, sortDirection }) =>
              allowsSorting ? (
                <div className="flex max-w-full min-w-0 items-center gap-1 overflow-hidden">
                  <div className="min-w-0 truncate">{column.header}</div>
                  {sortDirection !== undefined ? (
                    sortDirection === "ascending" ? (
                      <IconArrowDown className="size-3 shrink-0 text-gray-400" />
                    ) : (
                      <IconArrowUp className="size-3 shrink-0 text-gray-400" />
                    )
                  ) : null}
                </div>
              ) : (
                <div className="truncate">{column.header}</div>
              )
            }
          </Column>
        )}
      </TableHeader>

      <TableBody
        className={styles.body()}
        renderEmptyState={() => (
          <EntityTableEmptyState
            className={styles.empty()}
            isLoading={props.isLoading}
            isError={props.isError}
          >
            {props.emptyMessage}
          </EntityTableEmptyState>
        )}
      >
        <Collection
          items={entities}
          dependencies={[props.columns, props.getRowClassName]}
        >
          {(entity) => (
            <Row columns={props.columns} className={styles.row()}>
              {(column) => (
                <Cell
                  style={pinnedColumnStyles.get(column.id)}
                  className={styles.cell({
                    isLastRow: entity === entities.at(-1),
                    isPinnedToLeft: column.pinnedToLeft,
                    isLastPinnedToLeft: column.id === lastPinnedColumnId,
                    className: props.getRowClassName?.(entity),
                  })}
                  textValue={column.getTextValue?.(entity)}
                >
                  {column.renderCell(entity)}
                </Cell>
              )}
            </Row>
          )}
        </Collection>

        {canLoadMore ? (
          <Row>
            <Cell colSpan={props.columns.length}>
              <div ref={loadMoreSentinelRef}>
                <TableSkeletonContent rowCount={1} />
              </div>
            </Cell>
          </Row>
        ) : null}
      </TableBody>
    </Table>
  );
};

export interface EntityTableRowLinkProps extends Omit<
  LinkProps,
  "children" | "className" | "href"
> {
  /** Destination for the row link. */
  href: string;
  /** Additional classes for the link, merged with the row-overlay styles. */
  className?: string;
  children: ReactNode;
}

/**
 * A link to render inside an {@link EntityTable} cell (typically the row-header
 * column) when the table has `hasRowLinks`. Its invisible `::before` overlay
 * expands to cover the whole row — see {@link nestedControlsStyle} — so the
 * entire row acts as a single real link.
 *
 * Because it renders a real anchor, users can cmd/ctrl/right/middle-click the
 * row to open the link in a new tab. Any other interactive content in the row
 * (buttons, menus, secondary links) must be layered above the overlay with
 * `nestedControlsStyle.secondaryControl()`, otherwise the overlay will swallow
 * its clicks.
 */
export const EntityTableRowLink: FC<EntityTableRowLinkProps> = ({
  className,
  ...props
}): ReactNode => {
  const renderLink = useLinkRender();

  return (
    <Link
      {...props}
      className={nestedControlsStyle.primaryControl({ className })}
      render={props.isDisabled ? undefined : renderLink}
    />
  );
};
